import { useIsPresent } from 'framer-motion';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/** Returns false when a finish is already under way. */
type FinishHandler = () => boolean;

export interface OnboardingExitContextValue {
  canFinish: boolean;
  finish: FinishHandler;
  registerFinish: (handler: FinishHandler) => () => void;
}

const OnboardingExitContext = createContext<OnboardingExitContextValue | null>(null);

const NO_FINISH: OnboardingExitContextValue = {
  canFinish: false,
  finish: () => false,
  registerFinish: () => () => {},
};

export const OnboardingExitProvider = ({ children }: { children: ReactNode }) => {
  // A ref, not state: step handlers change identity on every render.
  const handlerRef = useRef<FinishHandler | null>(null);
  const [canFinish, setCanFinish] = useState(false);

  const finish = useCallback(() => handlerRef.current?.() ?? false, []);
  const registerFinish = useCallback((handler: FinishHandler) => {
    handlerRef.current = handler;
    setCanFinish(true);
    return () => {
      if (handlerRef.current !== handler) return;
      handlerRef.current = null;
      setCanFinish(false);
    };
  }, []);

  const value = useMemo(
    () => ({ canFinish, finish, registerFinish }),
    [canFinish, finish, registerFinish],
  );
  return <OnboardingExitContext.Provider value={value}>{children}</OnboardingExitContext.Provider>;
};

export function useOnboardingExit(): OnboardingExitContextValue {
  return useContext(OnboardingExitContext) ?? NO_FINISH;
}

/** Offers `finish` to the exit X while the caller is on screen, withdrawn as soon as its exit animation starts. */
export function useRegisterOnboardingFinish(finish: FinishHandler): void {
  const { registerFinish } = useOnboardingExit();
  const isPresent = useIsPresent();
  const finishRef = useRef(finish);
  finishRef.current = finish;

  useEffect(() => {
    if (!isPresent) return;
    return registerFinish(() => finishRef.current());
  }, [registerFinish, isPresent]);
}
